/* eslint-disable react-hooks/exhaustive-deps */
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  AgentView,
  CallableFunctionName,
  ExportToCsv,
  Firebase,
  PayBy,
  PublishedResidualOptionsResults,
  useApiContainer,
} from '@ozark/common';
import {Column} from '@ozark/common/api/Column';
import {CancelOperationMessage} from '@ozark/common/api/Constants';
import {
  ActiveFilter,
  AutoCompleteInputBase,
  currentFormatter,
  Table as DataTable,
  FilterOption,
  forceActiveFilter,
  Loading,
  Title,
  useReportingPageStyles,
} from '@ozark/common/components';
import {getChildAgentsByUid, getGroupAgents} from '@ozark/common/hooks/useAgents';
import {
  AgentPayoutReportItem,
  AgentPayoutResponse,
} from '@ozark/functions/src/functions/express/private/types/Residual';
import {CancelTokenSource} from 'axios';
import {parse} from 'date-fns';
import {format} from 'date-fns-tz';
import {isEmpty} from 'lodash';
import {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {RESIDUALS_AGENT} from '../../constants/routes';
import {useStore} from '../../store/helpers';

const Filters: FilterOption[] = [
  {
    id: 'yearMonth',
    column: 'yearMonth',
    label: 'Date',
    type: 'text',
    force: true,
    operators: [
      {
        id: '__eq',
        label: 'equals',
      },
    ],
  },
];

export const AgentPayout = () => {
  const api = useApiContainer();
  const history = useHistory();
  const classes = useReportingPageStyles();
  const {authProfile, isUserGroupAdmin, isUserMember} = useStore();
  const [agentPayouts, setAgentPayouts] = useState<AgentPayoutResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setCancelTokenSource] = useState<CancelTokenSource | undefined>();
  const [filters, setFilters] = useState<{[_: string]: ActiveFilter}>({});
  const [agents, setAgents] = useState<AgentView[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentView | undefined>();
  const [yearMonths, setYearMonths] = useState<string[] | null>(null);
  const [selectedYearMonth, setSelectedYearMonth] = useState<string>();

  const getAgentPayouts = () => {
    setIsLoading(true);

    const cancelSource = api?.residuals.getCancelTokenSource();
    setCancelTokenSource(prev => {
      // Check if there are any previous pending requests
      if (prev !== undefined) {
        console.log('cancel');
        prev.cancel(CancelOperationMessage);
      }
      return cancelSource;
    });

    const agentIds: string[] = selectedAgent ? [selectedAgent.id] : agents.map(a => a.id);

    api?.residuals
      .getAgentPayoutResiduals(Object.values(filters), agentIds, cancelSource?.token)
      .then((result: AgentPayoutResponse | null) => {
        setAgentPayouts(result || null);
        setIsLoading(false);
      })
      .catch((err: any) => {
        if (err?.message === CancelOperationMessage) {
          return;
        }
        console.error(err);
        setAgentPayouts(null);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    (async () => {
      const getPublishedResidualOptions = Firebase.functions.httpsCallable(
        CallableFunctionName.getPublishedResidualOptions
      );
      const result: {data: PublishedResidualOptionsResults} = await getPublishedResidualOptions({});
      if (!result?.data?.options) {
        setIsLoading(false);
        return;
      }

      const options = result.data.options.sort();
      setSelectedYearMonth(options[options.length - 1]);
      setYearMonths(options);
      if (!options.length) {
        setIsLoading(false);
      }

      const forcedFilter = forceActiveFilter(
        Filters,
        'yearMonth',
        '__eq',
        options[options.length - 1]
      );
      setFilters({yearMonth: forcedFilter});
    })();
  }, []);

  useEffect(() => {
    if (!selectedYearMonth || !agents.length) return;
    getAgentPayouts();
  }, [filters, selectedAgent, selectedYearMonth, agents]);

  useEffect(() => {
    if (authProfile.data) {
      const isMember = isUserMember();
      const isAdmin = isUserGroupAdmin();

      /* If it's a group admin, group agents' payouts are available for viewing */
      if (isAdmin) {
        const {group} = authProfile.data ?? {};
        if (group?.id) {
          getGroupAgents(group.id).then(agents => {
            if (agents?.length) {
              setAgents(agents);
            }
          });
        }
      }
      /* If it's a member and a master agent, sub agents' payouts are available for viewing */
      if (isMember) {
        const {id, subAgentUids} = authProfile.data ?? {};
        if (id && subAgentUids?.length > 0) {
          getChildAgentsByUid(id).then(agents => {
            if (agents?.length) {
              setAgents([authProfile.data as AgentView, ...agents]);
            }
          });
        }
      }
    }
  }, [authProfile.data, isUserGroupAdmin, isUserMember]);

  const handleDateChange = (event: any) => {
    const yearMonth = event.target.value;
    setSelectedYearMonth(yearMonth);
    const forcedFilter = forceActiveFilter(Filters, 'yearMonth', '__eq', yearMonth);
    setFilters({...filters, yearMonth: forcedFilter});
  };

  const handleDeleteFilter = (id: string) => () => {
    const _filters = {...filters};
    delete _filters[id];
    setFilters(_filters);
  };

  const exportToCsv = () => {
    const options = {
      fieldSeparator: ',',
      filename: `agent-payout-${selectedYearMonth}.csv`,
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: false,
      title: `Agent Payout - ${selectedYearMonth}`,
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
    };

    const data = agentPayouts?.data.map(p => ({
      agent: p.agentName,
      agentPayout: p.agentPayout ? p.agentPayout.toString() : '0',
      masterAgent: p.masterAgentName ?? '',
      subAgentPayout: p.subAgentPayout ? p.subAgentPayout.toString() : '0',
      subAgentPayBy: formatPayBy(p.subAgentPayBy),
    }));

    const exporter = new ExportToCsv(options);

    exporter.generateCsv(data);
  };

  const getFullName = (agent: AgentView) =>
    `${agent.firstName || ''} ${agent.lastName || ''}`.trim();

  const agentNameComparer = (a: AgentView, b: AgentView) =>
    getFullName(a).localeCompare(getFullName(b));

  const getAgentNames = (agent: AgentView): string => {
    return `${agent?.firstName} ${agent?.lastName}`.trim();
  };

  const getAgentDropDownTitle = (agent?: AgentView): string => {
    return agent ? getAgentNames(agent) : 'All Agents';
  };

  const formatYearMonth = (yearMonth: string): string => {
    const date = parse(yearMonth as string, 'yyyyMM', new Date());
    return format(date, 'MMMM, yyyy');
  };

  const formatPayBy = (subAgentPayBy: string | null): string => {
    switch (subAgentPayBy) {
      case PayBy.payByGroup:
        return 'Group';
      case PayBy.payByMasterAgent:
        return 'Master Agent';
      default:
        return '';
    }
  };

  const goToAgentResiduals = (agentId: string) => {
    history.push(RESIDUALS_AGENT, {
      agentId,
      yearMonth: selectedYearMonth,
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!yearMonths || yearMonths.length === 0) {
    return (
      <Box mt={6}>
        <Typography variant="body1" align="center">
          No residuals have been added.
        </Typography>
      </Box>
    );
  }

  return (
    <div className={classes.root}>
      <Title breadcrumbs={[<Typography variant="body1">Agent Payout</Typography>]}>
        <div className={classes.grow} />

        {agents && agents?.length > 0 && (
          <>
            <AutoCompleteInputBase
              selected={selectedAgent}
              setSelected={(agent?: AgentView) => setSelectedAgent(agent)}
              icon
              placeholder={agents?.length > 0 ? 'Select agent...' : 'No agents found'}
              options={agents}
              comparer={agentNameComparer}
              getOptionLabel={(agent: AgentView) => getAgentDropDownTitle(agent)}
              onItemSelect={(agent: AgentView | null) => setSelectedAgent(agent ?? undefined)}
            />

            <Divider orientation="vertical" className={classes.divider} flexItem />
          </>
        )}

        {yearMonths && (
          <TextField
            value={selectedYearMonth}
            onChange={handleDateChange}
            variant="standard"
            InputProps={{
              classes: {
                input: classes.selectInput,
              },
              disableUnderline: true,
            }}
            select
          >
            {yearMonths.map(e => {
              return (
                <MenuItem key={e} value={e}>
                  {formatYearMonth(e)}
                </MenuItem>
              );
            })}
          </TextField>
        )}

        {agentPayouts && !isEmpty(agentPayouts.data) && (
          <>
            <Divider orientation="vertical" className={classes.divider} flexItem />
            <Button onClick={exportToCsv}>Export</Button>
          </>
        )}
      </Title>

      {agentPayouts && isEmpty(agentPayouts.data) && (
        <Typography className={classes.noContent}>No Residuals</Typography>
      )}

      {agentPayouts && !isEmpty(agentPayouts.data) && (
        <>
          <Grid container spacing={2} direction="row" alignItems="stretch">
            <Grid item xs={12}>
              {filters && !isEmpty(filters) && (
                <IconButton disabled size="large">
                  <FilterListIcon />
                </IconButton>
              )}

              {filters &&
                Object.keys(filters).map(key => {
                  const filter = filters[key];

                  if (filter.option.id === 'yearMonth') {
                    return (
                      <Chip
                        key={`${key}-${filter.operator.id}`}
                        className={classes.chip}
                        label={
                          <span>
                            <b>{filter.option.label}</b> {filter.operator.label} '
                            <b>{formatYearMonth(filter.value as string)}</b>'
                          </span>
                        }
                        variant="outlined"
                        onDelete={filter.option.force ? undefined : handleDeleteFilter(key)}
                      />
                    );
                  }

                  return (
                    <Chip
                      key={`${key}-${filter.operator.id}`}
                      className={classes.chip}
                      label={
                        <span>
                          <b>{filter.option.label}</b> {filter.operator.label} '
                          <b>{`${filter.value}`}</b>'
                        </span>
                      }
                      variant="outlined"
                      onDelete={filter.option.force ? undefined : handleDeleteFilter(key)}
                    />
                  );
                })}
            </Grid>
          </Grid>

          <Box component={Paper} overflow="auto" mt={2}>
            <DataTable
              noWrap={true}
              columns={
                [
                  {
                    id: 'agentName',
                    label: 'Agent',
                    sortable: false,
                    numeric: false,
                    align: 'left',
                    selector: (row: AgentPayoutReportItem) => {
                      const isAdmin = isUserGroupAdmin();
                      return isAdmin ? (
                        <a
                          href={RESIDUALS_AGENT}
                          onClick={e => {
                            e.preventDefault();
                            goToAgentResiduals(row.agentId);
                          }}
                        >
                          {row.agentName}
                        </a>
                      ) : (
                        row.agentName
                      );
                    },
                  },
                  {
                    id: 'agentPayout',
                    label: 'Agent Payout',
                    sortable: false,
                    numeric: true,
                    align: 'center',
                    selector: (row: AgentPayoutReportItem) =>
                      currentFormatter.format(Number(row.agentPayout) || 0),
                  },
                  {
                    id: 'masterAgentName',
                    label: 'Master Agent',
                    sortable: false,
                    numeric: false,
                    align: 'center',
                  },
                  {
                    id: 'subAgentPayout',
                    label: 'Sub Agent Payout',
                    sortable: false,
                    numeric: true,
                    align: 'center',
                    selector: (row: AgentPayoutReportItem) =>
                      currentFormatter.format(Number(row.subAgentPayout) || 0),
                  },
                  {
                    id: 'subAgentPayBy',
                    label: 'Pay By',
                    sortable: false,
                    numeric: false,
                    align: 'center',
                    selector: (row: AgentPayoutReportItem) => formatPayBy(row.subAgentPayBy),
                  },
                ] as Column<AgentPayoutReportItem>[]
              }
              rows={agentPayouts.data}
              onRowClick={() => {}}
              paginate={false}
              stickyHeader
              scrollableBody
              customHeight="75vh"
            />
          </Box>
        </>
      )}
    </div>
  );
};
